<template>
  <b-navbar toggleable="lg" type="dark" variant="info" id="navbar" :style="{ '--environment-color-background': environment_color_background }">
    <b-navbar-brand ><a target="_new" href="https://www.initekconsulting.com/fleet-integrations"><img style="margin-top:-5px" width="40" src="favicon-white.png"/></a> Fleet Integrations</b-navbar-brand>
    <!-- icons always visible, even on mobile -->
    <div class="nav-always-visible">
      <div class="nav-icon-group">
        <!-- settings icon -->
        <b-nav-item class="nav-icon-btn" title="Settings"
          v-if="user.role == 'admin' || user.role == 'customer-admin' || user.role == 'tbn-admin'"
          :to="{ name: 'settings' }">
          <i class="fa fa-cog"></i>
        </b-nav-item>
        <!-- help center icon -->
        <b-nav-item class="nav-icon-btn" title="Help Center"
          v-if="user.role == 'admin' || (user.features[user.tenant] && user.features[user.tenant].help_center_enabled !== false)"
          :to="{ name: 'help_center' }">
          <i class="fa fa-question-circle"></i>
        </b-nav-item>
        <!-- user dropdown -->
        <b-nav-item-dropdown ref="navUserDropdown" right no-caret id="nav-user-dropdown" class="nav-icon-btn" :style="{ '--environment-color-button': environment_color_button }" @shown="onProfileDropdownShown">
          <template slot="button-content">
            <span class="profile-avatar" :title="user.full_name || user.username">
              {{ userInitials }}
              <span v-if="unreadCount > 0" class="profile-avatar-badge" :title="unreadCount + ' unread'">{{ unreadBadgeText }}</span>
            </span>
          </template>
          <!-- identity card: the visual star of the popup — avatar mirrored
               from the nav trigger at ~2x size, with name/role/email
               stacked to the right. turns the popup header from a
               two-line text block into a proper "profile card" header. -->
          <div class="profile-dropdown-header">
            <span class="profile-dd-avatar" :title="user.full_name || user.username">
              {{ userInitials }}
            </span>
            <div class="profile-dd-identity">
              <div class="profile-name-row">
                <span class="profile-display-name">{{ user.full_name || user.username }}</span>
                <!-- role tag: static icon+label pill for most users. for a
                     sys-admin the ADMIN tag is interactive — clicking it flips
                     view_as and the tag turns gray with a MUGGLE/wand label,
                     so the admin can preview the app as a non-admin from
                     anywhere without leaving the page. -->
                <button
                  v-if="user.role === 'admin'"
                  type="button"
                  class="profile-role-tag profile-role-tag--interactive"
                  :class="viewAsNonAdmin ? 'profile-role-tag-muggle' : 'profile-role-tag-admin'"
                  :title="viewAsNonAdmin ? 'Switch back to the admin view' : 'Preview the app as a non-admin'"
                  @click.stop.prevent="toggleAdminPreview"
                  @mousedown.stop.prevent
                >
                  <i class="fa" :class="viewAsNonAdmin ? 'fa-magic fa-flip-horizontal' : 'fa-shield'" aria-hidden="true"></i>
                  <span>{{ viewAsNonAdmin ? 'MUGGLE' : 'ADMIN' }}</span>
                </button>
                <span
                  v-else
                  class="profile-role-tag"
                  :class="'profile-role-tag-' + (user.role || 'other')"
                >
                  <i class="fa" :class="roleIconFor(user.role)" aria-hidden="true"></i>
                  <span>{{ roleDisplayLabel(user.role) }}</span>
                </span>
              </div>
              <div class="profile-email">{{ user.username }}</div>
            </div>
            <!-- compact icon-only action buttons stacked in the top-right
                 of the header card. Manage Profile on top, Logout below.
                 title attrs carry the label for hover + accessibility so
                 the small square chips stay clean. -->
            <div class="profile-dd-actions">
              <b-button
                size="sm"
                variant="outline-secondary"
                :to="{ name: 'user_detail', query: { username: user.username } }"
                @click.native="closeProfileDropdown"
                class="profile-dd-btn"
                title="Manage Profile"
                aria-label="Manage Profile"
              >
                <i class="fa fa-user-o" aria-hidden="true"></i>
              </b-button>
              <b-button
                id="nav-log-out"
                size="sm"
                variant="outline-danger"
                @click="logout()"
                class="profile-dd-btn"
                title="Logout"
                aria-label="Logout"
              >
                <i class="fa fa-sign-out" aria-hidden="true"></i>
              </b-button>
            </div>
          </div>
          <b-dropdown-divider></b-dropdown-divider>
          <!-- notifications section (FI-1432): iOS Messages-style card
               list. shows at most 5 cards with a "See all" link routing
               to the full /notifications page. each card has a type-
               colored icon puck, title + preview, and relative time;
               unread rows get a steelblue leading accent rail + pale
               blue tint (mirrors .admin-only-row's gold/cream pattern). -->
          <div class="nc-msg-section">
            <div class="nc-msg-header">
              <span class="nc-msg-title">Notifications</span>
              <b-button
                v-if="unreadCount > 0"
                size="sm"
                variant="outline-secondary"
                class="nc-msg-mark-all-btn"
                @click.stop="handleMarkAllRead"
              >
                <i class="fa fa-check-circle-o" aria-hidden="true"></i>
                Mark all read
              </b-button>
            </div>
            <div v-if="!notifications.length" class="nc-msg-empty">
              <i class="fa fa-bell-o" aria-hidden="true"></i>
              <div>No notifications yet</div>
            </div>
            <div v-else class="nc-msg-list">
              <a v-for="n in notifications.slice(0, 5)"
                 :key="n.notification_id"
                 :href="n.link || '#'"
                 class="nc-msg-card"
                 :class="{ 'nc-msg-card-unread': !n.read_at, ['nc-msg-card-' + typeStyle(n.type)]: true }"
                 @click.stop.prevent="handleNotificationClick(n)">
                <span class="nc-msg-puck">
                  <!-- report_complete: stroked document + checkmark glyph
                       (pairs with the outlined puck language); other
                       types stay on FA icons. kept in sync with
                       notifications_list.vue so both surfaces read the
                       same. -->
                  <svg v-if="n.type === 'report_complete'" class="nc-puck-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <path d="M14 2v6h6"/>
                    <polyline points="8 14 11 17 16 12"/>
                  </svg>
                  <template v-else>
                    <span v-if="_isSvgIconWrap(iconForNotificationRow(n))" class="ni-svg" v-html="_svgIconHtmlWrap(iconForNotificationRow(n))" aria-hidden="true"></span>
                    <i v-else class="fa" :class="iconForNotificationRow(n)" aria-hidden="true"></i>
                  </template>
                </span>
                <div class="nc-msg-body">
                  <div class="nc-msg-row-top">
                    <span class="nc-msg-heading">{{ n.title }}</span>
                    <span class="nc-msg-time">{{ relativeTime(n.created_at) }}</span>
                  </div>
                  <!-- body_preview is rendered as html so support_comment cards
                       can bold the commenter's name (FI-1642). all server-side
                       paths that build body_preview html-escape user content
                       before composing the string; other types' body_previews
                       are plain text so they render the same as before. -->
                  <div v-if="n.body_preview" class="nc-msg-preview" v-html="n.body_preview"></div>
                </div>
              </a>
            </div>
            <!-- see-all only when the user has at least one notification;
                 an empty inbox doesn't need a "see all of nothing" link. -->
            <div v-if="notifications.length" class="nc-msg-footer">
              <b-button
                :to="{ name: 'notifications_list' }"
                variant="outline-primary"
                size="sm"
                block
                class="nc-msg-see-all-btn"
                @click.native="closeProfileDropdown"
              >
                <span>See all notifications</span>
                <i class="fa fa-angle-right" aria-hidden="true"></i>
              </b-button>
            </div>
          </div>
        </b-nav-item-dropdown>
      </div>
      <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
    </div>

    <b-collapse id="nav-collapse" is-nav>
      <b-navbar-nav :style="{ '--environment-color-button': environment_color_button }">

        <template v-if="tenant_type =='FASTTRAK'">
          <b-nav-item :to="{ name: 'dashboard' }">Dashboard</b-nav-item>
          <b-nav-item :to="{ name: 'vehicle_map' }">Map</b-nav-item>
          <b-nav-item :to="{ name: 'vehicle_trip_history' }">Trips</b-nav-item>
          <b-nav-item :to="{ name: 'actuals_fasttrak' }">Bookings</b-nav-item>
          <b-nav-item :to="{ name: 'alert_list' }">Alerts</b-nav-item >
          <b-nav-item :to="{ name: 'vehicle_status_dvir_fasttrak' }">VSM</b-nav-item>
        </template>

        <template v-if="tenant_type =='FLEET'">
          <b-nav-item :to="{ name: 'dashboard' }">Dashboard</b-nav-item>
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item v-if="user.features[this.user.tenant].bus_sos_enabled" :to="{ name: 'sos_list' }">Bus SOS Manager</b-nav-item>
        </template>

        <template v-if="tenant_type =='CM-FLEET'">
          <b-nav-item :to="{ name: 'dashboard' }">Dashboard</b-nav-item>
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item :to="{ name: 'report_dashboard' }">Reports</b-nav-item>
        </template>

        <template v-if="tenant_type =='CM' && (user.role == 'dispatch' || user.role == 'garage' || user.role == 'wash-bay')">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item v-if="user.features[this.user.tenant].vehicle_status_management_dvir_enabled" :to="{ name: 'vehicle_status_dvir' }">Vehicle Status Manager</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_enabled" :to="{ name: 'vehicle_status' }">Vehicle Status Manager</b-nav-item>
          <b-nav-item v-if="user.role == 'dispatch' && user.features[this.user.tenant].alerts_enabled" :to="{ name: 'group_message_sender' }">Group Message Sender</b-nav-item>
        </template>

        <template v-if="tenant_type =='CM' && (user.role == 'garage')">
          <b-nav-item :to="{ name: 'report_dashboard' }">Reports</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='CM' && user.role == 'booking-customer'">
          <b-nav-item :to="{ name: 'booking_customer_list_tbn' }">Bookings</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='CM' && user.role == 'driver'">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item :to="{ name: 'trip_sheet_standalone' }">Vehicle Departure Board</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='CM' && user.role == 'customer-care'">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
<!--          <b-nav-item :to="{ name: 'vehicle_trip_history' }">Vehicle Trip History</b-nav-item>-->
          <b-nav-item v-if="telematics_type=='SAMSARA'" :to="{ name: 'vehicle_route_history_standalone' }">Vehicle Route History</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='CM' && user.role == 'manager'">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item :to="{ name: 'report_dashboard' }">Reports</b-nav-item>
          <b-nav-item v-if="user.features[this.user.tenant].vehicle_status_management_dvir_enabled" :to="{ name: 'vehicle_status_dvir' }">VSM</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_enabled" :to="{ name: 'vehicle_status' }">VSM</b-nav-item>
          <b-nav-item v-if="user.features[this.user.tenant].alerts_enabled" :to="{ name: 'group_message_sender' }">Group Message Sender</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='CM'">

          <b-nav-item :to="{ name: 'dashboard' }">Dashboard</b-nav-item>

          <b-nav-item-dropdown id="nav-dpdwn" >
            <template slot="button-content">Maps</template>
            <b-dropdown-item :to="{ name: 'vehicle_map' }">Fleet Map</b-dropdown-item>
            <b-dropdown-item v-if="user.role == 'admin'" :to="{ name: 'vehicle_map_future' }">Future Map</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'vehicle_trip_history' }">Vehicle Trip History</b-dropdown-item>
            <b-dropdown-item v-if="telematics_type=='SAMSARA'" :to="{ name: 'vehicle_route_history_standalone' }">Vehicle Route History</b-dropdown-item>
            <b-dropdown-item :to="{ path: '/booking-track/' + this.user.tenant }">Vehicle Booking Map</b-dropdown-item>
          </b-nav-item-dropdown>

          <b-nav-item :to="{ name: 'actuals' }">Bookings</b-nav-item>
          <b-nav-item :to="{ name: 'alert_list' }">Alerts</b-nav-item >
          <b-nav-item v-if="user.features[this.user.tenant].vehicle_status_management_maint_enabled" :to="{ name: 'vehicle_status_maint' }">VSM</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_dvir_enabled" :to="{ name: 'vehicle_status_dvir' }">VSM</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_enabled" :to="{ name: 'vehicle_status' }">VSM</b-nav-item>
          <b-nav-item v-if="user.role != 'admin' && user.features[this.user.tenant].vehicle_status_management_enabled && !user.features[this.user.tenant].views_reports_enabled"
                      :to="{ name: 'vehicle_status_history' }">VSM History</b-nav-item>

          <b-nav-item :to="{ name: 'report_dashboard' }">Reports</b-nav-item>

          <b-nav-item-dropdown id="nav-dpdwn" v-if="user.features[this.user.tenant].smart_dispatching_enabled" >
            <template slot="button-content">Dispatch</template>
            <b-dropdown-item :to="{ name: 'vehicle_map_future' }">Future Map</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'driver_list' }">Smart Driver Mgmt</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'smart_route_assignments' }">Smart Route Assigner</b-dropdown-item>
          </b-nav-item-dropdown>

        </template>

        <template v-if="tenant_type =='TBN' && (this.user.tenant.indexOf('parent') != -1) && user.role != 'booking-customer'">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item :to="{ name: 'report_dashboard' }">Reports</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='TBN' && user.role == 'booking-customer'">
          <b-nav-item :to="{ name: 'booking_customer_list_tbn' }">Bookings</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='TBN' && (user.role == 'dispatch' || user.role == 'garage' || user.role == 'wash-bay')">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item v-if="user.features[this.user.tenant].vehicle_status_management_dvir_enabled" :to="{ name: 'vehicle_status_dvir_tbn' }">Vehicle Status Manager</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_maint_enabled" :to="{ name: 'vehicle_status_maint_tbn' }">Vehicle Status Manager</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_enabled" :to="{ name: 'vehicle_status_tbn' }">Vehicle Status Manager</b-nav-item>
          <b-nav-item v-if="user.role == 'dispatch' && user.features[this.user.tenant].alerts_enabled" :to="{ name: 'group_message_sender_tbn' }">Group Message Sender</b-nav-item>
          <b-nav-item-dropdown id="nav-dpdwn" v-if="user.features[this.user.tenant].smart_dispatching_enabled" >
            <template slot="button-content">Dispatch</template>
            <b-dropdown-item :to="{ name: 'vehicle_map_future' }">Future Map</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'driver_list_tbn' }">Smart Driver Mgmt</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'smart_route_assignments_tbn' }">Smart Route Assigner</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'trip_linker_report_tbn' }">Smart Trip Linker</b-dropdown-item>
          </b-nav-item-dropdown>
        </template>

        <template v-else-if="tenant_type =='TBN' && user.role == 'driver' && user.tenant == 'dattco-413f464d'">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item :to="{ name: 'driver_bid_board_tbn' }">Driver Bid Board</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='TBN' && user.role == 'driver'">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='TBN' && user.role == 'manager'">
          <b-nav-item :to="{ name: 'vehicle_map' }">Fleet Map</b-nav-item>
          <b-nav-item :to="{ name: 'actuals_tbn' }">Bookings</b-nav-item>
          <b-nav-item :to="{ name: 'report_dashboard' }">Reports</b-nav-item>
          <b-nav-item v-if="user.features[this.user.tenant].vehicle_status_management_dvir_enabled" :to="{ name: 'vehicle_status_dvir_tbn' }">VSM</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_maint_enabled" :to="{ name: 'vehicle_status_maint_tbn' }">VSM</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_enabled" :to="{ name: 'vehicle_status_tbn' }">VSM</b-nav-item>
          <b-nav-item v-if="user.features[this.user.tenant].vehicle_status_management_enabled" :to="{ name: 'vehicle_status_history' }">VSM History</b-nav-item>
          <b-nav-item v-if="user.features[this.user.tenant].alerts_enabled" :to="{ name: 'group_message_sender_tbn' }">Group Message Sender</b-nav-item>
        </template>

        <template v-else-if="tenant_type =='TBN'">

          <b-nav-item :to="{ name: 'dashboard' }">Dashboard</b-nav-item>

          <b-nav-item-dropdown id="nav-dpdwn" >
            <template slot="button-content">Maps</template>
            <b-dropdown-item :to="{ name: 'vehicle_map' }">Fleet Map</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'vehicle_map_future' }">Future Map</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'vehicle_trip_history' }">Vehicle Trip History</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'vehicle_route_history_standalone' }">Vehicle Route History</b-dropdown-item>
            <b-dropdown-item :to="{ path: '/booking-track/' + this.user.tenant + '?type=TBN' }">Vehicle Booking Map</b-dropdown-item>
          </b-nav-item-dropdown>

          <b-nav-item :to="{ name: 'actuals_tbn' }">Bookings</b-nav-item>
          <b-nav-item :to="{ name: 'alert_list' }">Alerts</b-nav-item >
          <b-nav-item v-if="user.features[this.user.tenant].vehicle_status_management_dvir_enabled" :to="{ name: 'vehicle_status_dvir_tbn' }">VSM</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_maint_enabled" :to="{ name: 'vehicle_status_maint_tbn' }">VSM</b-nav-item>
          <b-nav-item v-else-if="user.features[this.user.tenant].vehicle_status_management_enabled" :to="{ name: 'vehicle_status_tbn' }">VSM</b-nav-item>
          <b-nav-item v-if="user.role != 'admin' && user.features[this.user.tenant].vehicle_status_management_enabled && !user.features[this.user.tenant].views_reports_enabled"
                      :to="{ name: 'vehicle_status_history' }">VSM History</b-nav-item>

          <b-nav-item :to="{ name: 'report_dashboard' }">Reports</b-nav-item>

          <b-nav-item-dropdown id="nav-dpdwn" v-if="user.features[this.user.tenant].smart_dispatching_enabled" >
            <template slot="button-content">Dispatch</template>
            <b-dropdown-item v-if="user.role == 'admin' || (this.user.tenant == 'anderson-759g468f' && user.role == 'customer-admin' )" :to="{ name: 'driver_scorecard' }">Driver Scorecard</b-dropdown-item>
            <b-dropdown-item v-if="user.role == 'admin' || (user.features[user.tenant] && user.features[user.tenant].fleet_assistant_enabled !== false)" :to="{ name: 'fleet_assistant' }">Fleet Assistant</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'vehicle_map_future' }">Future Map</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'driver_list_tbn' }">Smart Driver Mgmt</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'smart_route_assignments_tbn' }">Smart Route Assigner</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'trip_linker_report_tbn' }">Smart Trip Linker</b-dropdown-item>
          </b-nav-item-dropdown>

          <b-nav-item-dropdown id="nav-dpdwn" v-if="user.features[this.user.tenant].data_services_compare_drivers_enabled || user.role == 'admin'" >
            <template slot="button-content">Sync</template>
            <b-dropdown-item :to="{ name: 'compare_drivers' }">Compare Driver Lists</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'compare_driver_profiles' }">Compare Driver Profiles</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'compare_drivers_history' }">Compare Driver History</b-dropdown-item>
          </b-nav-item-dropdown>

          <b-nav-item-dropdown id="nav-dpdwn" v-if="user.tenant == 'dattco-413f464d' && (user.role == 'admin' || user.role == 'customer-admin')" >
            <template slot="button-content">Driver Bid</template>
            <b-dropdown-item :to="{ name: 'driver_bid_manage_board_tbn' }">Driver Bid Management</b-dropdown-item>
          </b-nav-item-dropdown>
        </template>

      </b-navbar-nav>

      <!-- tenant selector + desktop icons (hidden on mobile, shown on desktop) -->
      <b-navbar-nav class="ml-auto nav-desktop-icons" style="align-items: center;">
        <b-nav-item-dropdown v-if="user.role == 'admin'" right no-caret class="nav-icon-btn nav-admin-dropdown" title="Admin Tools">
          <template slot="button-content"><i class="fa fa-wrench"></i></template>
          <b-dropdown-item :to="{ name: 'admin_dashboard' }">Summary Dashboard</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'tenant_manager' }">All Tenants</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'api_usage_dashboard' }">API Usage</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'process_manager_list' }">Process Manager</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'actuals_simulator' }">Actuals Simulator</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'driver_scorecard_comparison' }">Driver Scorecard Comparison</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'url_shortener' }">URL Shortener</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'cache_manager' }">Cache Manager</b-dropdown-item>
        </b-nav-item-dropdown>
        <span v-if="visibleTenantOptions.length > 1" class="d-flex align-items-center justify-content-center">
          <b-form-select class="nav-tabs nav-link" :style="{ '--environment-color-button': environment_color_button }" style="font-size: 20px; font-weight: bold" v-model="user.tenant" :options="visibleTenantOptions" @change="set_tenant_id"/>
        </span>
        <span v-else class="d-flex align-items-center justify-content-center" style="color:white; font-size: 20px; font-weight: bold; padding: 0.5rem 1rem;">
          {{ currentTenantLabel }}
        </span>
      </b-navbar-nav>

      <!-- tenant picker at bottom of drawer on mobile.
           @click/@mousedown/@touchstart are stopped from bubbling because
           BV's <b-collapse is-nav> auto-closes the drawer whenever any
           descendant with `.nav-link` is clicked (intended for menu items
           — it treats a tap as "user picked something, close the menu").
           the tenant picker is a <select>, not a menu item, and needs to
           stay visible long enough for the native picker to appear and
           for the user to choose. stopping propagation keeps the drawer
           open while the picker is in use. -->
      <div class="nav-mobile-tenant" @click.stop @mousedown.stop @touchstart.stop>
        <span v-if="visibleTenantOptions.length > 1" class="d-flex align-items-center">
          <b-form-select class="nav-tabs nav-link" :style="{ '--environment-color-button': environment_color_button }" style="font-size: 16px; font-weight: bold; margin-left: 0;" v-model="user.tenant" :options="visibleTenantOptions" @change="set_tenant_id"/>
        </span>
        <span v-else style="color:white; font-size: 18px; font-weight: bold; padding: 8px 0;">
          {{ currentTenantLabel }}
        </span>
      </div>
    </b-collapse>
  </b-navbar>
</template>

<script>
import { mapState } from 'vuex'
import {
  mark_all_notifications_read as cm_mark_all_notifications_read,
  record_notification_action as cm_record_notification_action,
} from '../networking/apis'
import {
  tbn_mark_all_notifications_read,
  tbn_record_notification_action,
} from '../networking/tbn_apis'

import router from '../router'
import { roleIconForInternal, roleLabelForInternal } from '../helpers/role_display'
import { iconForNotification, isSvgIcon, customSvgHtml } from '../utils/notification_icons'
export default {
  name: 'navbar',
  props: {
    authenticated: Boolean,
  },
  created() {
    // register global BV dropdown hooks once — see _onAnyDropdownShow
    // for the why. $root is the BV event bus; these fire for every
    // b-dropdown / b-nav-item-dropdown in the app.
    this.$root.$on('bv::dropdown::show', this._onAnyDropdownShow)
    this.$root.$on('bv::dropdown::shown', this._onAnyDropdownShown)
  },
  beforeDestroy() {
    this.$root.$off('bv::dropdown::show', this._onAnyDropdownShow)
    this.$root.$off('bv::dropdown::shown', this._onAnyDropdownShown)
  },
  methods: {
    logout() {
      router.push("/login")
      this.$emit("authenticated", false);
    },
    set_tenant_id(id) {
      this.user.tenant = id
      localStorage.setItem('selected_tenant',id)
    },
    // flip the admin's preview-as-customer state. persisted via vuex to
    // localStorage so it survives reloads until toggled back.
    toggleAdminPreview() {
      const next = this.viewAsNonAdmin ? null : 'non-admin'
      this.$store.commit('profile/set_view_as', next)
    },
    // profile chip shows the viewer's own role to themselves — use the
    // internal variant of the shared role registry so staff see "SYS
    // ADMIN" / fa-shield here. helpers/role_display.js also exports a
    // customer-facing variant used elsewhere (ticket comments, etc).
    roleIconFor(role) {
      return roleIconForInternal(role)
    },
    roleDisplayLabel(role) {
      return roleLabelForInternal(role)
    },
    // notification center (FI-1432)
    // the 60s background poll is good enough for passive updates, but
    // the moment the user opens the profile dropdown they expect to see
    // fresh state. fire an immediate refresh through the root-level
    // `nc-refresh` bus so App.vue re-runs its poll with the active
    // tenant context (which already owns the CM vs TBN dispatch).
    onProfileDropdownShown() {
      try { this.$root.$emit('nc-refresh') } catch (_) {}
    },
    // BV's b-nav-item-dropdown / b-dropdown auto-focus the opened
    // .dropdown-menu <ul>. when that <ul> is taller than the viewport
    // (settings gear has ~15 items, Reports menus on some tenants
    // have ~14 entries, both exceed ~700px), the browser's focus-
    // scroll-into-view scrolls the page down by the navbar height
    // so the menu's top lands at viewport top. the fix: snapshot
    // window.scrollY before show, restore it after shown (once the
    // unwanted focus-scroll has already run).
    //
    // listening on the $root BV event bus covers every dropdown in
    // the app in one place — no need to wire @show/@shown on each
    // of the 13+ navbar dropdowns (Reports per tenant_type, Dispatch,
    // Compare, etc). harmless on dropdowns that didn't jump (the
    // restore is a no-op when scrollY is already where it was).
    _onAnyDropdownShow() {
      this._scrollYBeforeNavDropdown = window.scrollY
    },
    _onAnyDropdownShown() {
      const y = this._scrollYBeforeNavDropdown
      if (y == null) return
      if (window.scrollY !== y) window.scrollTo(0, y)
    },
    // programmatic close: used by notification-row clicks and the
    // "See all notifications" link so the popup doesn't linger while
    // the user is navigating somewhere else.
    closeProfileDropdown() {
      try {
        if (this.$refs.navUserDropdown && typeof this.$refs.navUserDropdown.hide === 'function') {
          this.$refs.navUserDropdown.hide(true)
        }
      } catch (_) {}
    },
    // delegates to the shared notification-icon registry — prefers the
    // per-row icon override (set by the announcement composer) and falls
    // back to the type default. report_complete still uses the inline
    // stroked SVG above this template branch, so we don't need a special
    // case here.
    iconForNotificationRow(n) {
      return iconForNotification(n)
    },
    _isSvgIconWrap(name) {
      return isSvgIcon(name)
    },
    _svgIconHtmlWrap(name) {
      return customSvgHtml(name)
    },
    // picks the iOS-card color variant for a notification type. keeps
    // the puck/preview-bg palette consistent with the .doc-badge family
    // and the admin role chips.
    typeStyle(type) {
      if (type === 'announcement') return 'announcement'
      if (type === 'report_complete') return 'report'
      if (type === 'service_alert') return 'service-alert'
      if (type === 'service_resolved') return 'service-resolved'
      if (type === 'support_comment') return 'support'
      return 'generic'
    },
    relativeTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      const diff = Math.max(0, Date.now() - d.getTime()) / 1000
      if (diff < 60) return 'just now'
      if (diff < 3600) return Math.floor(diff / 60) + 'm ago'
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago'
      if (diff < 604800) return Math.floor(diff / 86400) + 'd ago'
      return d.toLocaleDateString()
    },
    async handleMarkAllRead() {
      // optimistic local update, then fire api
      this.$store.commit('notifications/mark_all_read_local')
      try {
        const fn = this.isTbn ? tbn_mark_all_notifications_read : cm_mark_all_notifications_read
        await fn(this.user.tenant)
      } catch (_) { /* best effort; next poll will reconcile */ }
    },
    async handleNotificationClick(n) {
      if (!n || !n.notification_id) return
      // close the profile popup immediately so the user sees the
      // destination view without the dropdown lingering on top.
      try {
        if (this.$refs.navUserDropdown && typeof this.$refs.navUserDropdown.hide === 'function') {
          this.$refs.navUserDropdown.hide(true)
        }
      } catch (_) {}
      this.$store.commit('notifications/apply_action_local', n.notification_id)
      try {
        const fn = this.isTbn ? tbn_record_notification_action : cm_record_notification_action
        await fn(this.user.tenant, n.notification_id)
      } catch (_) { /* best effort; next poll will reconcile */ }
      // navigate after recording the action so actioned_at reflects the click.
      // defensive: '/' is the router's login redirect and empty/whitespace
      // has no destination — either would boot the user to login, so skip.
      const dest = (n.link || '').trim()
      if (dest && dest !== '/') {
        try {
          if (dest.startsWith('http')) {
            window.location.href = dest
          } else {
            this.$router.push(dest)
          }
        } catch (_) { /* router may reject if already on this route */ }
      }
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
      unreadCount: state => state.notifications.unreadCount,
      notifications: state => state.notifications.notifications,
    }),
    viewAsNonAdmin() {
      return this.viewAs === 'non-admin'
    },
    // FI-1432: the active tenant determines which backend the notification
    // center polls and posts to. tbn tenants hit tbn_apis; everything else
    // (CM, FLEET, CM-FLEET, FASTTRAK) hits apis.
    isTbn() {
      return this.tenant_type === 'TBN'
    },
    unreadBadgeText() {
      if (this.unreadCount > 9) return '9+'
      return String(this.unreadCount)
    },
    is_admin() {
      return this.user.role === 'admin'
    },
    // admin-gated UI in the nav should disappear when a sys-admin previews as
    // a non-admin via the MUGGLE toggle. use this anywhere the MUGGLE state
    // should influence visibility; keep `user.role === 'admin'` only for the
    // MUGGLE button itself (which must stay clickable to flip back).
    isEffectiveAdmin() {
      return this.user.role === 'admin' && !this.viewAsNonAdmin
    },
    // tenant picker simulation for the MUGGLE toggle: a regular customer sees
    // only the tenants within their fleet group, where "fleet" = tenants
    // sharing the first-segment prefix of the tenant id (e.g. `anderson-*`).
    // matches the server-side multi-tenant prefix detection in
    // fleet_service._get_multi_tenant_prefixes.
    visibleTenantOptions() {
      const all = (this.user && this.user.tenant_options) || []
      if (!this.viewAsNonAdmin) return all
      const current = this.user && this.user.tenant
      if (!current || typeof current !== 'string') return all
      const prefix = current.split('-', 1)[0]
      if (!prefix) return all
      const sameFleet = all.filter(opt => {
        const v = typeof opt === 'object' ? opt.value : opt
        return typeof v === 'string' && v.split('-', 1)[0] === prefix
      })
      return sameFleet.length > 0 ? sameFleet : all
    },
    // label shown in place of the tenant picker when the user (real or
    // previewed-as muggle) has nothing to switch between. prefers the
    // currently-selected option text so the label stays accurate when an
    // admin toggles MUGGLE on a tenant different from their primary.
    currentTenantLabel() {
      const opts = this.visibleTenantOptions
      const tid = this.user && this.user.tenant
      const match = opts.find(o => (typeof o === 'object' ? o.value : o) === tid)
      if (match && typeof match === 'object' && match.text) return match.text
      return (this.user && this.user.tenant_name) || tid || ''
    },
    telematics_type() {
      return this.user.telematics_types[this.user.tenant]
    },
    tenant_type() {
      return this.user.types[this.user.tenant]
    },
    environment_color_button() {
      return '#6095c1'
    },
    environment_color_background() {
      return 'steelblue'
    },
    userInitials() {
      const name = this.user.full_name || this.user.username || ''
      if (name.includes('@')) {
        // email: take first letter of the local part
        return name.split('@')[0].charAt(0).toUpperCase()
      }
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
      }
      return name.charAt(0).toUpperCase() || '?'
    },
  }

}
</script>

<style>

.bg-info {
  background-color: var(--environment-color-background, steelblue) !important;
}

#navbar {
  background-color: var(--environment-color-background, steelblue) !important;
  padding: 1rem !important;
}

.nav-tabs {
  background-color: var(--environment-color-background, steelblue) !important;
}

.nav-link {
  background-color: var(--environment-color-button, #6095c1) !important;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 20px;
  font-weight: bold;
  color: white !important;
  margin-left: .8rem;
  justify-content: center !important;
  padding: .3rem !important;
}

/* the tenant picker uses .nav-link + .custom-select; Bootstrap's .custom-select
   brings its own border/radius/height that overrides .nav-link and leaves the
   picker visually taller and with different corners than the sibling menu
   items. force it back to the nav-link geometry so the navbar reads as one
   consistent row. */
select.nav-link.custom-select {
  border: 1px solid #ccc !important;
  border-radius: 6px !important;
  height: auto !important;
  line-height: 1.5;
}

.navbar-brand {
  color: white !important;
  font-size: 22px !important;
  font-weight: bold;
}

.nav-icon-btn .nav-link {
  padding: 2px 4px !important;
  margin-left: 6px !important;
  min-width: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  line-height: 1 !important;
  background: none !important;
  border: none !important;
  box-shadow: none !important;
}


.nav-icon-btn .nav-link:hover,
.nav-icon-btn .nav-link:focus {
  background: none !important;
}

.nav-icon-btn .nav-link .fa {
  font-size: 28px;
  color: rgba(255, 255, 255, 0.85);
  transition: color 0.15s;
}

.nav-icon-btn .nav-link:hover .fa {
  color: #fff;
}

.profile-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  transition: background 0.15s;
  /* caps-only initials look crown-high when flex-centered because the
     line box includes descender space that no cap ever uses. nudging the
     content down 1px via asymmetric padding shifts the visual weight
     into the optical center of the circle without changing the avatar's
     outer dimensions (padding-bottom default 0 keeps total height at 36). */
  padding-top: 2px;
  line-height: 1;
  /* anchor for the unread-count badge overlay */
  position: relative;
}

/* unread count pill on the avatar. tucked into the top-right corner with
   a small white ring to read cleanly against the steelblue navbar.
   hidden via v-if when unreadCount === 0. */
.profile-avatar-badge {
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #dc3545;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 0 0 2px var(--environment-color-background, steelblue);
  letter-spacing: 0;
}

.nav-icon-btn .nav-link:hover .profile-avatar {
  background: rgba(255, 255, 255, 0.3);
}

/* restore gap between tenant picker and first icon */
.nav-icon-btn:first-of-type .nav-link {
  margin-left: 12px !important;
}

/* identity card header — avatar on the left, name/role/email stack on
   the right. soft gradient pulls the eye to the card; the rest of the
   popup flows into a neutral white below. */
.profile-dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px 12px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  white-space: nowrap;
}

/* inverted-puck style: outlined circle + pale steelblue fill +
   colored initials, same design family as the announcement-detail
   elevated puck. softened border so the outline sits closer to the
   tint and the avatar reads as a cohesive shape — initials + halo
   still carry the identity. */
.profile-dd-avatar {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 2px solid #b8d0e4;
  background: #eef4fb;
  color: var(--environment-color-background, steelblue);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding-top: 2px;
  line-height: 1;
  box-sizing: border-box;
  box-shadow:
    0 2px 5px rgba(70, 130, 180, 0.16),
    0 1px 2px rgba(70, 130, 180, 0.08);
}

.profile-dd-identity {
  flex: 1;
  min-width: 0;
  white-space: normal;
}

.profile-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.profile-display-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

/* role tag in the profile popup — icon + uppercase label pill. for sys-
   admins the tag is a clickable button that flips the Preview-as-Customer
   state (ADMIN ⇄ MUGGLE). variants set their own color pair via CSS
   custom properties so the shape + sizing stay consistent across roles. */
.profile-role-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 10px;
  line-height: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  border-radius: 4px;
  border: 1px solid var(--role-tag-color, #6c757d);
  background: var(--role-tag-bg, #e9ecef);
  color: var(--role-tag-fg, #333);
  white-space: nowrap;
}
.profile-role-tag .fa {
  font-size: 10px;
}
.profile-role-tag--interactive {
  cursor: pointer;
  user-select: none;
  transition: filter 0.12s ease, box-shadow 0.12s ease;
}
.profile-role-tag--interactive:hover {
  filter: brightness(0.96);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}
.profile-role-tag--interactive:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 122, 199, 0.25);
}
.profile-role-tag--interactive:active {
  filter: brightness(0.9);
}

/* ADMIN — sys-admin orange, matching the admin-only button palette. */
.profile-role-tag-admin {
  --role-tag-color: #b58100;
  --role-tag-bg: #fff4d1;
  --role-tag-fg: #7a5400;
}
/* MUGGLE — gray, the "previewing as a non-admin" state. */
.profile-role-tag-muggle {
  --role-tag-color: #8a8f97;
  --role-tag-bg: #e9ecef;
  --role-tag-fg: #4a4f55;
}

.profile-role-tag-customer-admin {
  --role-tag-color: #1e7eeb;
  --role-tag-bg: #e7f1ff;
  --role-tag-fg: #0b3d91;
}
.profile-role-tag-customer {
  --role-tag-color: #6c757d;
  --role-tag-bg: #eef0f3;
  --role-tag-fg: #3a3f45;
}
.profile-role-tag-booking-customer {
  --role-tag-color: #0aa69a;
  --role-tag-bg: #d9f5f1;
  --role-tag-fg: #0a6e67;
}
.profile-role-tag-driver {
  --role-tag-color: #28a745;
  --role-tag-bg: #e9f6ee;
  --role-tag-fg: #155724;
}
.profile-role-tag-customer-care {
  --role-tag-color: #6f42c1;
  --role-tag-bg: #efe7fa;
  --role-tag-fg: #3d2873;
}
.profile-role-tag-manager {
  --role-tag-color: #3d4fb0;
  --role-tag-bg: #e4e8f8;
  --role-tag-fg: #27336d;
}
.profile-role-tag-dispatch {
  --role-tag-color: #c48b00;
  --role-tag-bg: #fff1d1;
  --role-tag-fg: #6b4b00;
}
.profile-role-tag-garage {
  --role-tag-color: #5a6473;
  --role-tag-bg: #e6e9ee;
  --role-tag-fg: #2d3440;
}
.profile-role-tag-wash-bay {
  --role-tag-color: #17a2b8;
  --role-tag-bg: #dff5f8;
  --role-tag-fg: #0b6373;
}
.profile-role-tag-other {
  --role-tag-color: #6c757d;
  --role-tag-bg: #e9ecef;
  --role-tag-fg: #495057;
}

.profile-email {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.nav-admin-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 3px;
  background: #fef3e2;
  color: #c45e00;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* hybrid mobile nav: icons always visible in top bar, full nav in drawer */
.nav-always-visible {
  display: contents;
}

.nav-icon-group {
  display: none;
}

.nav-mobile-tenant {
  display: none;
}

@media (max-width: 991px) {
  /* tighten the navbar's outer padding on mobile so we don't waste 32px
     of horizontal space that pushed the icon group onto its own line
     under the brand. everything else in this block works to keep the
     brand + icons + hamburger together on one row. */
  #navbar {
    padding: 0.6rem 0.75rem !important;
    flex-wrap: wrap; /* still needed so the collapse drawer opens below */
  }

  /* brand — sized to split the difference between a too-compact pass
     (16px / 28px that left touch targets cramped) and a too-big pass
     (18px / 32px that forced the icon row to wrap). at 17px / 30px the
     brand is ~195px wide, leaving ~200px on a 430px viewport for the
     icon group + hamburger. */
  .navbar-brand {
    font-size: 17px !important;
    margin-right: 0 !important;
    padding-right: 0 !important;
    white-space: nowrap;
  }
  .navbar-brand img {
    width: 30px !important;
    margin-top: -3px !important;
  }

  /* show the always-visible icon group on mobile */
  .nav-always-visible {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 2px;
  }

  .nav-icon-group {
    display: flex;
    align-items: center;
    gap: 4px;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .nav-icon-group .nav-item {
    list-style: none;
  }

  /* icon buttons — at 24px icons + tight 4x5 padding we get ~34x32
     clickable each, which comes out to ~148px for the 4-button group
     (icons + hamburger). combined with the ~200px brand we fit in
     ~372px total, well under iPhone 17 Pro Max's 430px viewport. */
  .nav-icon-btn .nav-link {
    padding: 4px 5px !important;
    margin-left: 2px !important;
  }
  .nav-icon-btn .nav-link .fa {
    font-size: 24px !important;
  }
  .profile-avatar {
    width: 32px;
    height: 32px;
    font-size: 14px;
    /* carry the optical-centering nudge from the desktop rule through */
    padding-top: 2px;
    line-height: 1;
  }

  /* style hamburger to match the icon group */
  .nav-always-visible .navbar-toggler {
    border: none !important;
    padding: 4px 5px !important;
    background: none !important;
    box-shadow: none !important;
    outline: none !important;
  }

  .nav-always-visible .navbar-toggler-icon {
    width: 24px;
    height: 24px;
  }

  /* hide the desktop icons + tenant picker inside the collapse */
  .nav-desktop-icons {
    display: none !important;
  }

  /* show the mobile tenant picker at bottom of drawer */
  .nav-mobile-tenant {
    display: block;
    padding: 10px 0 4px;
    border-top: 1px solid rgba(255,255,255,0.2);
    margin-top: 8px;
  }

  /* mobile: profile dropdown takes the full page width under the navbar
     instead of anchoring to the avatar (which bleeds the right-aligned
     menu off the left edge on narrow screens). override popper's inline
     transform so the menu snaps to the viewport edges. fixed positioning
     keeps it attached to the navbar even when the page is scrolled. */
  #nav-user-dropdown .dropdown-menu {
    position: fixed !important;
    top: var(--navbar-bottom, 56px);
    left: 0 !important;
    right: 0 !important;
    width: 100vw;
    max-width: 100vw;
    max-height: calc(100vh - var(--navbar-bottom, 56px));
    overflow-y: auto;
    margin: 0;
    border-radius: 0;
    border-left: none;
    border-right: none;
    /* bottom-edge shadow so the drop-down reads as an elevated sheet
       sitting over the page content below. spread (24) widens the cast
       past the viewport edges so there's no taper at the left/right
       corners, and the y-offset matches the spread so the shadow box
       starts flush with the element's top edge — no upward bleed into
       the navbar. blur is kept small so the feathering stays downward. */
    border-bottom: none;
    box-shadow: 0 24px 14px 24px rgba(0, 0, 0, 0.14);
    transform-origin: top center;
  }
  /* quick expand: drops down from under the navbar and fades in. only
     runs when the menu becomes visible (BV toggles .show and flips
     display:none→block). 160ms feels snappy; the slight overshoot curve
     gives it a light bounce. */
  #nav-user-dropdown .dropdown-menu.show {
    animation: profile-dd-mobile-expand 160ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }
  @keyframes profile-dd-mobile-expand {
    from {
      opacity: 0;
      transform: scaleY(0.88) translateY(-8px);
    }
    to {
      opacity: 1;
      transform: scaleY(1) translateY(0);
    }
  }
}

@media (min-width: 992px) {
  /* on desktop: show icons inline in the navbar, pushed to far right */
  .nav-always-visible {
    display: flex;
    align-items: center;
    gap: 4px;
    order: 999;
    margin-left: 12px;
  }

  .nav-icon-group {
    display: flex;
    align-items: center;
    gap: 4px;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .nav-icon-group .nav-item {
    list-style: none;
  }

  /* hide hamburger and mobile tenant on desktop */
  .nav-always-visible .navbar-toggler {
    display: none !important;
  }

  .nav-mobile-tenant {
    display: none;
  }
}

/* FI-1432 notifications section: iOS Messages-inspired card list
   inside the profile dropdown. five cards max, then a "See all" link
   routes to the full /notifications page. color puck per type comes
   from the muted-pastel family (.doc-badge vocabulary in FA settings)
   so the in-product visual grammar stays consistent. */
.nc-msg-section {
  padding: 6px 0 2px;
  min-width: 360px;
  max-width: 420px;
}

.nc-msg-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px 10px;
  color: #6c757d;
}

.nc-msg-title {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nc-msg-mark-all-btn {
  font-size: 11px;
  padding: 2px 8px;
  line-height: 1.4;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.nc-msg-mark-all-btn .fa {
  font-size: 11px;
}

.nc-msg-mark-all {
  font-size: 11px;
  text-transform: none;
  letter-spacing: 0;
  color: #3273dc;
  text-decoration: none;
}

.nc-msg-mark-all:hover { text-decoration: underline; }

.nc-msg-empty {
  padding: 22px 16px 18px;
  text-align: center;
  color: #8a8a8a;
  font-size: 13px;
}

.nc-msg-empty .fa {
  display: block;
  font-size: 22px;
  margin-bottom: 6px;
  color: #cdd6e0;
}

.nc-msg-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 10px;
}

.nc-msg-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px 10px 18px;
  border-radius: 10px;
  background: transparent;
  color: #1a202c;
  text-decoration: none;
  transition: background 0.12s;
}

.nc-msg-card:hover,
.nc-msg-card:focus {
  background: #f4f7fb;
  color: #1a202c;
  text-decoration: none;
}

/* unread = pale blue tint + a flush full-height steelblue rail on the
   left. matches the notifications list page rail (4px, flush, square
   rectangle) so the popup and the dedicated page read as the same
   surface. absolutely-positioned pseudo (not box-shadow) keeps the
   rail a hard rectangle even though the card itself is rounded —
   `overflow: hidden` on the card clips the rail's top/bottom into
   the card's border-radius so it doesn't poke past the rounded
   corners. */
/* unread = steelblue rail only. dropped the pale-blue card tint
   2026-04-29 because the rail + the read-card text dimming already
   carry the unread signal cleanly; the bg tint was redundant and
   competed with the bg of the surrounding popover.
   no overflow:hidden on purpose — letting the absolute ::before
   draw over the card's rounded-corner area keeps the rail a hard
   rectangle (sharp top + bottom) even though the card is rounded.
   the rail visually clips the card's top-left/bottom-left corner
   curve. */
.nc-msg-card-unread::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: steelblue;
  border-radius: 0;
}

/* group adjacent unread cards into a single contiguous block: collapse
   the parent's 4px gap between them and flatten the corners that face
   each other. visually reads as "this batch is unread" rather than a
   stack of independent unread cards. read cards stay rounded on every
   side (the gap reappears around them since they fall outside this
   selector). uses :has() for the first-of-pair selector — supported
   on every modern browser our stack ships to (Vue 2 + BV2 baseline). */
.nc-msg-card-unread + .nc-msg-card-unread {
  margin-top: -4px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.nc-msg-card-unread:has(+ .nc-msg-card-unread) {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

/* read-state dimming — title + preview + time recede so unread rows
   pull the eye. opacity rather than a flat color override so dark and
   light theme contexts both work. matches the "read items step back"
   intent on the notifications list page (where puck borders soften
   for the same reason — same visual goal, different mechanism per
   surface). */
.nc-msg-card:not(.nc-msg-card-unread) .nc-msg-heading,
.nc-msg-card:not(.nc-msg-card-unread) .nc-msg-preview,
.nc-msg-card:not(.nc-msg-card-unread) .nc-msg-time {
  opacity: 0.62;
}
.nc-msg-card:not(.nc-msg-card-unread):hover .nc-msg-heading,
.nc-msg-card:not(.nc-msg-card-unread):hover .nc-msg-preview,
.nc-msg-card:not(.nc-msg-card-unread):hover .nc-msg-time {
  opacity: 1;
}

/* type-colored icon puck (rounded-square). outlined-box variant: light
   pastel fill, colored 1.5px border, colored icon — per-type tokens
   override the generic defaults below. */
.nc-msg-puck {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border: 1.5px solid #94a3b8;
  color: #64748b;
  font-size: 15px;
  box-sizing: border-box;
}

.nc-puck-svg {
  width: 18px;
  height: 18px;
}

.nc-msg-card-report .nc-msg-puck {
  background: #ecfdf5;
  border-color: #10b981;
  color: #10b981;
}
.nc-msg-card-announcement .nc-msg-puck {
  background: #f3f0ff;
  border-color: #7c5dd0;
  color: #7c5dd0;
}
.nc-msg-card-service-alert .nc-msg-puck {
  background: #fef3c7;
  border-color: #d97706;
  color: #d97706;
}
/* service_resolved — green "all clear" puck. shares the report-complete
   green family but uses #059669 for the border/icon so it doesn't read
   as just another report row. */
.nc-msg-card-service-resolved .nc-msg-puck {
  background: #ecfdf5;
  border-color: #059669;
  color: #059669;
}
.nc-msg-card-generic .nc-msg-puck {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #64748b;
}
/* support comments: sky-blue. distinct from the steelblue accent rail
   on unread cards but in the same blue family so the affordance reads
   as "communication" rather than "alert" or "broadcast". matches the
   notifications_list page. */
.nc-msg-card-support .nc-msg-puck {
  background: #e0f2fe;
  border-color: #0284c7;
  color: #0284c7;
}

/* read-state: soften the outline toward the tint so the puck recedes
   into the card. the icon is still readable but stops drawing the eye
   away from the unread rows. border only, bg + icon color stay put so
   the type is still identifiable at a glance. transition on the puck
   so hover smoothly reverts to full saturation. */
.nc-msg-puck {
  transition: border-color 0.12s ease;
}
.nc-msg-card-report:not(.nc-msg-card-unread) .nc-msg-puck {
  border-color: #a7f3d0;
}
.nc-msg-card-announcement:not(.nc-msg-card-unread) .nc-msg-puck {
  border-color: #d8cffb;
}
.nc-msg-card-service-alert:not(.nc-msg-card-unread) .nc-msg-puck {
  border-color: #fde68a;
}
.nc-msg-card-service-resolved:not(.nc-msg-card-unread) .nc-msg-puck {
  border-color: #a7f3d0;
}
.nc-msg-card-generic:not(.nc-msg-card-unread) .nc-msg-puck {
  border-color: #cbd5e1;
}
.nc-msg-card-support:not(.nc-msg-card-unread) .nc-msg-puck {
  border-color: #bae6fd;
}

/* hover on a read card: the puck pops back to its unread (full-color)
   border so the row reads as "interactive — same content as fresh".
   complements the .nc-msg-heading/preview/time opacity-1 hover rule
   above so the entire row hover-state is consistent. */
.nc-msg-card-report:not(.nc-msg-card-unread):hover .nc-msg-puck {
  border-color: #10b981;
}
.nc-msg-card-announcement:not(.nc-msg-card-unread):hover .nc-msg-puck {
  border-color: #7c5dd0;
}
.nc-msg-card-service-alert:not(.nc-msg-card-unread):hover .nc-msg-puck {
  border-color: #d97706;
}
.nc-msg-card-service-resolved:not(.nc-msg-card-unread):hover .nc-msg-puck {
  border-color: #059669;
}
.nc-msg-card-generic:not(.nc-msg-card-unread):hover .nc-msg-puck {
  border-color: #94a3b8;
}
.nc-msg-card-support:not(.nc-msg-card-unread):hover .nc-msg-puck {
  border-color: #0284c7;
}

.nc-msg-body {
  flex: 1;
  min-width: 0;
}

.nc-msg-row-top {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
}

.nc-msg-heading {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1a202c;
}

.nc-msg-time {
  flex: 0 0 auto;
  font-size: 11px;
  color: #8a8a8a;
  font-variant-numeric: tabular-nums;
}

.nc-msg-preview {
  font-size: 12px;
  color: #4a5568;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.nc-msg-footer {
  padding: 8px 16px 12px;
  margin-top: 2px;
}

/* b-button block sets display:block which kills flex/gap — keep text +
   chevron inline and give the chevron explicit left margin so the
   spacing doesn't depend on layout mode. slight padding bump + 14px
   font reads as a proper CTA, not a squeezed link. nested selector
   outranks bootstrap .btn-sm's padding rule. */
.nc-msg-footer .nc-msg-see-all-btn {
  font-size: 14px;
  font-weight: 600;
  padding: 10px 14px;
  text-align: center;
}
.nc-msg-footer .nc-msg-see-all-btn .fa {
  font-size: 12px;
  margin-left: 8px;
  vertical-align: baseline;
}

/* router-link buttons render as <a>, so the global
   `a { color: steelblue !important }` rule fights bootstrap's
   outline-primary :hover (which fills bg blue + text white).
   result was blue-on-blue = unreadable. force white text on
   any active/hover/focus state. same pattern as .profile-dd-btn
   below. */
.nc-msg-footer .nc-msg-see-all-btn:hover,
.nc-msg-footer .nc-msg-see-all-btn:focus,
.nc-msg-footer .nc-msg-see-all-btn:active,
.nc-msg-footer .nc-msg-see-all-btn.active,
.nc-msg-footer .nc-msg-see-all-btn:not(:disabled):not(.disabled):active {
  color: #fff !important;
}

.nc-msg-see-all {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 4px;
  font-size: 12px;
  font-weight: 600;
  color: #3273dc;
  text-decoration: none;
}

.nc-msg-see-all .fa { font-size: 14px; }

.nc-msg-see-all:hover {
  color: #1e59c0;
  text-decoration: none;
}

/* action buttons — stacked vertically in the top-right of the header
   card (Manage Profile above Logout). margin-left: auto pins the column
   to the right edge; flex-shrink: 0 keeps the buttons at natural width
   so a long display name can't push them off-screen. */
.profile-dd-actions {
  display: flex;
  flex-direction: row;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
  list-style: none;
  padding: 0;
}

/* icon-only compact chips — 30x30 rounded-square buttons carrying just
   the FA glyph; the label lives in title/aria-label so screen readers +
   hover tooltips still announce it. keeps the header card tight while
   leaving the identity block at its full visual weight. */
.profile-dd-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  font-size: 13px;
  line-height: 1;
  border-radius: 8px;
}

.profile-dd-btn .fa {
  font-size: 14px;
  margin: 0;
}

.profile-dd-btn .fa { font-size: 13px; }

/* manage profile is a router-link (<a>), so the global
   `a { color: steelblue !important }` rule overrides the
   outline-secondary hover color. restore bootstrap defaults. */
.profile-dd-btn.btn-outline-secondary {
  color: #6c757d !important;
}
.profile-dd-btn.btn-outline-secondary:hover,
.profile-dd-btn.btn-outline-secondary:focus,
.profile-dd-btn.btn-outline-secondary:active {
  color: #fff !important;
}

</style>
